import { X } from 'lucide-react';
import type { FeatureFlagDiff, FeatureFlagDiffEntry, FeatureFlagOp } from '@shared';
import { Badge, Button, Card } from '@design-system';

type PendingChangesCartProps = {
  diff: FeatureFlagDiff;
  operations: FeatureFlagOp[];
  isSaving: boolean;
  isBehindLatest: boolean;
  onRemoveOp: (index: number) => void;
  onClearAll: () => void;
};

export function PendingChangesCart({
  diff,
  operations,
  isSaving,
  isBehindLatest,
  onRemoveOp,
  onClearAll,
}: PendingChangesCartProps) {
  if (!diff.hasChanges) {
    return (
      <Card className="border border-dashed border-gray-200 bg-gray-50 p-4">
        <div className="text-muted-foreground text-sm">
          No pending changes. Toggle a flag to start a draft.
        </div>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-200 bg-white p-0">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Pending changes</span>
          <Badge
            variant="secondary"
            className="text-xs"
          >
            {diff.entries.length}
          </Badge>
          {isSaving && (
            <span className="text-muted-foreground text-xs">Saving…</span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          disabled={isSaving}
        >
          Clear all
        </Button>
      </div>

      {isBehindLatest && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          The live AppConfig has changed since this draft was started. Refresh the
          page before publishing — publishing will be rejected until the draft is
          rebased against the latest version.
        </div>
      )}

      <ul className="divide-y divide-gray-100">
        {diff.entries.map((entry, i) => (
          <li
            key={diffEntryKey(entry, i)}
            className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
          >
            <DiffEntryRow entry={entry} />
            <RemoveEntryButton
              entry={entry}
              operations={operations}
              onRemoveOp={onRemoveOp}
              disabled={isSaving}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DiffEntryRow({ entry }: { entry: FeatureFlagDiffEntry }) {
  switch (entry.kind) {
    case 'allAdvisors-set':
      return (
        <div className="flex min-w-0 items-center gap-2">
          <Badge
            variant="outline"
            className="text-xs"
          >
            all advisors
          </Badge>
          <code className="truncate font-mono text-xs">{entry.flagKey}</code>
          <ValueArrow previous={entry.previous} next={entry.next} />
        </div>
      );
    case 'allAdvisors-remove':
      return (
        <div className="flex min-w-0 items-center gap-2">
          <Badge
            variant="outline"
            className="text-xs"
          >
            all advisors
          </Badge>
          <code className="truncate font-mono text-xs">{entry.flagKey}</code>
          <ValueArrow previous={entry.previous} next={undefined} />
        </div>
      );
    case 'advisor-set':
      return (
        <div className="flex min-w-0 items-center gap-2">
          <code className="truncate font-mono text-xs text-gray-700">
            {entry.advisorId}
          </code>
          <code className="truncate font-mono text-xs">{entry.flagKey}</code>
          <ValueArrow previous={entry.previous} next={entry.next} />
        </div>
      );
    case 'advisor-remove':
      return (
        <div className="flex min-w-0 items-center gap-2">
          <code className="truncate font-mono text-xs text-gray-700">
            {entry.advisorId}
          </code>
          <code className="truncate font-mono text-xs">{entry.flagKey}</code>
          <ValueArrow previous={entry.previous} next={undefined} />
        </div>
      );
    case 'advisor-section-remove':
      return (
        <div className="flex min-w-0 items-center gap-2">
          <Badge className="bg-red-100 text-xs text-red-800">remove section</Badge>
          <code className="truncate font-mono text-xs text-gray-700">
            {entry.advisorId}
          </code>
        </div>
      );
    case 'client-set':
      return (
        <div className="flex min-w-0 items-center gap-2">
          <code className="truncate font-mono text-xs text-gray-700">{entry.clientId}</code>
          <code className="truncate font-mono text-xs">{entry.flagKey}</code>
          <ValueArrow previous={entry.previous} next={entry.next} />
        </div>
      );
  }
}

function ValueArrow({
  previous,
  next,
}: {
  previous: boolean | undefined;
  next: boolean | undefined;
}) {
  return (
    <span className="text-muted-foreground flex items-center gap-1 text-xs">
      <ValueChip value={previous} />
      <span>→</span>
      <ValueChip value={next} />
    </span>
  );
}

function ValueChip({ value }: { value: boolean | undefined }) {
  if (value === undefined) {
    return (
      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600">
        unset
      </span>
    );
  }
  if (value) {
    return (
      <span className="rounded bg-green-100 px-1.5 py-0.5 font-mono text-[10px] text-green-800">
        on
      </span>
    );
  }
  return (
    <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[10px] text-red-800">
      off
    </span>
  );
}

function RemoveEntryButton({
  entry,
  operations,
  onRemoveOp,
  disabled,
}: {
  entry: FeatureFlagDiffEntry;
  operations: FeatureFlagOp[];
  onRemoveOp: (index: number) => void;
  disabled: boolean;
}) {
  const opIndex = findOpIndexForEntry(entry, operations);
  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-muted-foreground h-6 w-6 p-0"
      disabled={disabled || opIndex < 0}
      onClick={() => opIndex >= 0 && onRemoveOp(opIndex)}
      aria-label="Remove this change"
    >
      <X className="h-3.5 w-3.5" />
    </Button>
  );
}

function diffEntryKey(entry: FeatureFlagDiffEntry, i: number): string {
  switch (entry.kind) {
    case 'allAdvisors-set':
    case 'allAdvisors-remove':
      return `${entry.kind}:${entry.flagKey}`;
    case 'advisor-set':
    case 'advisor-remove':
      return `${entry.kind}:${entry.advisorId}:${entry.flagKey}`;
    case 'advisor-section-remove':
      return `${entry.kind}:${entry.advisorId}`;
    case 'client-set':
      return `${entry.kind}:${entry.clientId}:${entry.flagKey}`;
    default:
      return String(i);
  }
}

function findOpIndexForEntry(
  entry: FeatureFlagDiffEntry,
  operations: FeatureFlagOp[],
): number {
  for (let i = operations.length - 1; i >= 0; i -= 1) {
    const op = operations[i];
    if (opMatchesEntry(op, entry)) return i;
  }
  return -1;
}

function opMatchesEntry(op: FeatureFlagOp, entry: FeatureFlagDiffEntry): boolean {
  if (entry.kind === 'allAdvisors-set' || entry.kind === 'allAdvisors-remove') {
    return (
      (op.type === 'SET_ALL_ADVISORS' || op.type === 'REMOVE_ALL_ADVISORS') &&
      op.flagKey === entry.flagKey
    );
  }
  if (entry.kind === 'advisor-set' || entry.kind === 'advisor-remove') {
    return (
      (op.type === 'SET_ADVISOR_FLAG' || op.type === 'REMOVE_ADVISOR_FLAG') &&
      op.advisorId === entry.advisorId &&
      op.flagKey === entry.flagKey
    );
  }
  if (entry.kind === 'advisor-section-remove') {
    // A section can disappear either from an explicit REMOVE_ADVISOR_SECTION op
    // or from REMOVE_ADVISOR_FLAG ops that empty the section (pruned by
    // applyFeatureFlagOperations). Either should be undoable from the cart.
    return (
      (op.type === 'REMOVE_ADVISOR_SECTION' || op.type === 'REMOVE_ADVISOR_FLAG') &&
      op.advisorId === entry.advisorId
    );
  }
  if (entry.kind === 'client-set') {
    return (
      op.type === 'SET_CLIENT_FLAG' &&
      op.clientId === entry.clientId &&
      op.flagKey === entry.flagKey
    );
  }
  return false;
}
